import { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem as MuiMenuItem,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import { MenuItem } from "src/business/restaurant/menu";
import {
  colorBorder,
  colorTextMuted,
  colorTextPrimary,
  colorChipBackground,
  colorChipText,
  colorChipBorder,
  colorError,
  colorWarning,
} from "src/design/tokens";
import { useTranslation } from "react-i18next";

interface Props {
  item: MenuItem;
  sectionName: string;
  isLastInSection: boolean;
  isDragging: boolean;
  canDrag: boolean;
  canUpdate: boolean;
  dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
  onEdit: (item: MenuItem, sectionName: string) => void;
  onDelete: (itemId: string | null) => void;
  onToggleFeatured: (item: MenuItem) => void;
}

export const MenuItemRow = ({
  item,
  sectionName,
  isLastInSection,
  isDragging,
  canDrag,
  canUpdate,
  dragHandleProps,
  onEdit,
  onDelete,
  onToggleFeatured,
}: Props) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        py: 2,
        borderBottom: isLastInSection ? "none" : `1px solid ${colorBorder}`,
        opacity: isDragging ? 0.35 : 1,
        backgroundColor: isDragging ? `${colorBorder}40` : "transparent",
        borderRadius: isDragging ? "6px" : 0,
        userSelect: "none",
      }}
    >
      <Box
        {...(canDrag && dragHandleProps ? dragHandleProps : {})}
        sx={{
          display: "flex",
          alignItems: "center",
          mt: 0.5,
          mr: 0.5,
          color: colorTextMuted,
          cursor: canDrag ? "grab" : "default",
          opacity: canDrag ? 1 : 0,
          pointerEvents: canDrag ? "auto" : "none",
          flexShrink: 0,
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 16 }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: colorTextPrimary }}>
            {item.name}
          </Typography>
          <Chip
            label={sectionName}
            size="small"
            sx={{
              fontSize: "0.6875rem",
              height: 20,
              backgroundColor: colorChipBackground,
              color: colorChipText,
              border: `1px solid ${colorChipBorder}`,
              borderRadius: "4px",
            }}
          />
        </Box>
        {item.description && (
          <Typography
            sx={{ fontSize: "0.8125rem", color: colorTextMuted, mb: 0.75, lineHeight: 1.4 }}
          >
            {item.description}
          </Typography>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: colorTextPrimary }}>
            {item.price} {t("menu.currency")}
          </Typography>
        </Box>
      </Box>

      {canUpdate && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1, mt: 0.25 }}>
          <IconButton
            size="small"
            onClick={() => onToggleFeatured(item)}
            title={t("menu.featured")}
            sx={{ color: item.featured ? colorWarning : colorTextMuted }}
          >
            {item.featured
              ? <StarIcon sx={{ fontSize: 18 }} />
              : <StarBorderIcon sx={{ fontSize: 18 }} />
            }
          </IconButton>

          <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}>
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { borderRadius: "10px", minWidth: 130 } }}
      >
        <MuiMenuItem
          onClick={() => { onEdit(item, sectionName); setAnchor(null); }}
          sx={{ fontSize: "0.875rem" }}
        >
          {t("common.edit")}
        </MuiMenuItem>
        <MuiMenuItem
          onClick={() => { onDelete(item.id); setAnchor(null); }}
          sx={{ fontSize: "0.875rem", color: colorError }}
        >
          {t("common.delete")}
        </MuiMenuItem>
      </Menu>
    </Box>
  );
};
